/**
 * Blog card component for article previews
 * Displays blog post thumbnail, title, date, and excerpt with lazy loading
 */

import { h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { getLowQualityPlaceholder, generateSrcSet } from 'libs/athos/components/ResultImage'

const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

export function BlogCard({ blog, className = '', featured = false }) {
  const articleImage = blog.image || blog.image_alt
  const [hasImage, setHasImage] = useState(false)

  useEffect(() => {
    if (blog.image.width && blog.image.height) {
      setHasImage(true)
    }

    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update()
    }
  }, [articleImage?.src, blog])

  const content = (
    <>
      <div className={`w-fit richtext mb-0.5 flex items-center gap-x-0.5 ${featured ? 'text-type-white-secondary' : 'text-type-black-secondary'}`}>
        {blog.tags.map((tag) => (
          <span key={tag} className="text-2xs w-fit uppercase">
            {tag}
          </span>
        ))}
      </div>

      <h3 className={`heading-xs font-medium mb-0.5 ${featured ? 'text-type-white-primary' : ''}`}>{blog.title}</h3>
      {blog.excerpt && (
        <div className={`w-fit richtext paragraph-xs ${featured ? 'text-type-white-primary' : ''}`}>
          {blog.excerpt}
        </div>
      )}
      <button type="button" className={`text-xs ${featured ? 'text-type-white-primary' : 'text-type-black-secondary'} w-fit link-under-line`}>
          <span>Read Article</span>
      </button>
    </>
  )

  return (
    <a href={blog.url} className={`group/blog-card flex flex-col gap-1.5 ${className}`} aria-label={`Read more about ${blog.title}`}>
      <div className="relative w-full aspect-[376/382] lg:aspect-3/4 bg-page-secondary overflow-hidden">
        <div className="absolute inset-0 children:size-full children:object-cover children:group-hover/blog-card:scale-[1.025] children:transition-all">
          {hasImage && articleImage && articleImage.src && (
            <img
              className="lazy size-full object-cover group-hover/blog-card:scale-[1.025] transition-all"
              src={getLowQualityPlaceholder(articleImage.src)}
              data-src={articleImage.src}
              data-srcset={generateSrcSet(articleImage.src, IMAGE_WIDTHS)}
              data-sizes="50vw"
              alt={articleImage.alt || blog.title}
              width={articleImage.width}
              height={articleImage.height}
            />
          )}
        </div>

        {featured && (
          <div className="absolute inset-x-0 bottom-0 flex flex-col px-1 pb-1 pt-4 bg-gradient-to-t from-type-black-primary/80 to-transparent pointer-events-none">
            {content}
          </div>
        )}
      </div>

      {!featured && (
        <div className="flex flex-col px-0.5">
          {content}
        </div>
      )}
    </a>
  )
}
